import { useState, useCallback } from 'react';
import { UseFormSetValue, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { TipShareDistributionConfig } from '../../types';
import { TipShareFormData, TipShareDistributionRequest } from '../types';
import { useUpdateTipShareDistributionConfigMutation } from '../../requests/mutations';

interface UseDisableDistributionParams {
  distributionConfig: TipShareDistributionConfig | null | undefined;
  enableTipShareDistribution: boolean;
  reset: UseFormReset<TipShareFormData>;
  setValue: UseFormSetValue<TipShareFormData>;
}

interface UseDisableDistributionReturn {
  showDisableDialog: boolean;
  isDisabling: boolean;
  handleToggleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleConfirmDisable: () => Promise<void>;
  handleCancelDisable: () => void;
}

/**
 * Manages the distribution enable/disable toggle flow for TipShareSettingsPage:
 * - Shows a confirmation dialog before disabling existing distribution config via API.
 * - Handles the PATCH call to deactivate distribution.
 * - Falls back to a local form value update when no backend data exists.
 */
export default function useDisableDistribution({
  distributionConfig,
  enableTipShareDistribution,
  reset,
  setValue,
}: UseDisableDistributionParams): UseDisableDistributionReturn {
  const { t } = useTranslation();
  const { setSnackbarState } = useSnackbar();
  const updateDistributionMutation = useUpdateTipShareDistributionConfigMutation();

  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;

      // If turning OFF while distribution is enabled and backend data exists → confirm via dialog
      if (!newValue && enableTipShareDistribution && distributionConfig) {
        setShowDisableDialog(true);
      } else {
        setValue('enableTipShareDistribution', newValue, { shouldDirty: true, shouldValidate: false });

        // When enabling for the first time (no backend data), default contribution toggle and checkboxes to ON
        if (newValue && !distributionConfig) {
          setValue('enableTipShareContribution', true, { shouldValidate: false });
          setValue('includeComps', true, { shouldValidate: false });
          setValue('includePromotions', true, { shouldValidate: false });
          setValue('includeTax', true, { shouldValidate: false });
        }
      }
    },
    [enableTipShareDistribution, distributionConfig, setValue]
  );

  const handleConfirmDisable = useCallback(async () => {
    setIsDisabling(true);
    try {
      const disableRequest: Partial<TipShareDistributionRequest> & { id?: string; isActive: boolean } = {
        id: distributionConfig?.id,
        isActive: false,
      };

      await updateDistributionMutation.mutateAsync(disableRequest as TipShareDistributionRequest);

      reset();
      setSnackbarState({ open: true, message: t('tipShare.disableSuccess'), color: 'success' });
      setShowDisableDialog(false);
    } catch {
      setSnackbarState({ open: true, message: t('tipShare.disableError'), color: 'error' });
      // Keep dialog open so user can retry or cancel
    } finally {
      setIsDisabling(false);
    }
  }, [distributionConfig?.id, updateDistributionMutation, reset, setSnackbarState, t]);

  const handleCancelDisable = useCallback(() => {
    setShowDisableDialog(false);
  }, []);

  return {
    showDisableDialog,
    isDisabling,
    handleToggleChange,
    handleConfirmDisable,
    handleCancelDisable,
  };
}
